/** (ア) 勤務時間がまだ計算されていない日。 */
export const STAMPED_KIND_PENDING = 'pending'
/** (イ) 出勤だけ打刻した日。 */
export const STAMPED_KIND_WORKING = 'working'
/** (ウ) 打刻して、勤務時間の計算まで記録された日（処理済みの打刻日）。判定は settledStampedOriginOf。 */
export const STAMPED_KIND_SETTLED = 'settled'

/**
 * 勤務表の1日の行が「打刻由来の行」か。
 *
 * ## 何のためか
 *
 * 打刻（ダッシュボードの出勤・退勤・休憩）は、開始・終了・休憩しか記録しない。
 * 勤務時間・時間外・夜勤・法定休日・控除、定時後の休憩、月の合計は、勤務表の画面が計算して保存する。
 * 打刻した日を、勤務表で同じ時刻を入れた日と同じ結果にするには、勤務表が
 * 「この行の休憩は、打刻が入れたままだ」と分かって、時刻欄へ入力したときと同じ計算をやり直す必要がある。
 * その見分けを、ここ1か所で行う（見える勤務表でも、画面に出さずに動かすときでも、同じ判定）。
 *
 * ## 判定（読み込んだときに、行ごとに決める）
 *
 * 一覧の行に付いてくる印で決める。
 *
 * - **(ア) 勤務時間がまだ計算されていない日**：開始・終了が入っていて日ごとの勤務時間が未保存
 *   （stamping_pending）、かつ、休憩が打刻の入れた値のまま（stamped_rest_untouched）。
 *   → 読み込みの最後に、休憩を計算し直す。
 * - **(イ) 出勤だけ打刻した日**：開始あり・終了なし・休憩が打刻の入れた値のまま、かつ
 *   （固定休憩を使う会社、または打刻で作られた行の手がかり stamped_row_hint がある）。
 *   → 保存済みの休憩を「手で直した休憩」として固定しない（勤務表で終了を入れたとき、
 *     退勤を打刻したときと同じ休憩になるように）。
 *
 * どちらにも当たらない行は、今までどおりに扱う。
 * （勤務時間の計算まで記録された打刻日のための (ウ) は、下の settledStampedOriginOf が決める。）
 *
 * ## 当てない行
 *
 * - 休憩が打刻の入れた値のままでない行（打刻のあと、勤務表で休憩を直した行）。
 *   「手で直した休憩は上書きしない」を守る。
 * - 時刻を隠して返された行（actual_hidden）と、画面が未来日として扱う行。
 *   時刻が空の行へ休憩を入れると、その月が保存できなくなる。
 * - 印が付いてこない応答（印を返さない版のサーバ）。全部の行が今までどおりになる。
 *
 * @param row     一覧の応答の1行
 * @param hasStart 画面の開始欄に値があるか（読み込んだ直後）
 * @param hasEnd   画面の終了欄に値があるか（読み込んだ直後）
 * @param future   画面がその日を未来日として扱うか
 * @returns 打刻由来の行なら { kind, fixedBreak, breakMinutes, nightBreakMinutes }、違えば null
 *   - kind：'pending'（ア）か 'working'（イ）
 *   - fixedBreak：固定休憩を使う会社か
 *   - breakMinutes / nightBreakMinutes：打刻した休憩・夜勤休憩の合計（分）
 */
export function stampedOriginOf (row, { hasStart, hasEnd, future } = {}) {
  if (!row || typeof row !== 'object') return null
  if (row.stamped_rest_untouched !== true) return null

  const kind = stampedKindOf(row, { hasStart, hasEnd, future })
  if (!kind) return null

  return {
    kind,
    fixedBreak: row.stamping_fixed_break === true,
    breakMinutes: wholeMinutes(row.stamped_break_minutes),
    nightBreakMinutes: wholeMinutes(row.stamped_night_break_minutes)
  }
}

/**
 * 打刻由来の行の種類（'pending'＝(ア)・'working'＝(イ)）。どちらでもなければ null。
 *
 * 休憩の印（stampedOriginOf）と夜勤休憩の印（stampedNightOriginOf）が、この同じ決め方を通る。
 * 「休憩が打刻の入れた値のままか」（stamped_rest_untouched）は、ここでは見ない（休憩の印だけが見る）。
 *
 * - 時刻を隠して返された行・画面が未来日として扱う行・行が無い日は null。
 * - (ア)：開始・終了があり、日ごとの勤務時間が未保存（stamping_pending）。
 * - (イ)：開始あり・終了なし、かつ（固定休憩を使う会社、または打刻で作られた行の手がかり stamped_row_hint）。
 */
export function stampedKindOf (row, { hasStart, hasEnd, future } = {}) {
  if (!row || typeof row !== 'object') return null
  if (future) return null
  if (row.actual_hidden === true) return null
  if (row.id === null || row.id === undefined) return null

  const fixedBreak = row.stamping_fixed_break === true
  if (row.stamping_pending === true && hasStart && hasEnd) return STAMPED_KIND_PENDING
  if (hasStart && !hasEnd && (fixedBreak || row.stamped_row_hint === true)) return STAMPED_KIND_WORKING
  return null
}

/**
 * 勤務表の1日の行の、夜勤休憩の印（夜勤休憩の 00:00 が、打刻の入れた値かを見分けるため）。
 *
 * ## 何のためか
 *
 * 休憩終了の打刻は、夜の休憩（22〜5時の休憩）が無くても、夜勤休憩へ 00:00 を入れる。勤務表は、
 * 夜の休憩を打刻していない打刻由来の行の 00:00 を「入力ではない値」として扱う（時刻を入れたら空へ戻す・
 * ファイル取り込みの「空欄だけに反映」で空欄と同じに数える。Roster.js の nightRestIsUnstampedZero）。
 *
 * 休憩の印（stampedOriginOf）は、休憩が打刻の入れた値のままの行にしか付かない。それで見ると、休憩という
 * 別の欄を直しただけで、夜勤休憩の 00:00 まで「利用者が入れた値」に変わってしまう。保存して開き直したあとも
 * 同じ。そこで夜勤休憩は、休憩を直したかに関係なく、この印で見分ける。
 *
 * ## 決め方
 *
 * 種類（pending／working）は、休憩の印と同じ stampedKindOf。違うのは、stamped_rest_untouched を見ないことだけ。
 * 夜勤休憩がいま 00:00 かどうかは、使う所（Roster.js）が画面の値で見る。
 *
 * - 打刻の印が付いてこない応答では null（今までどおり）。
 * - 処理済みの打刻日（stamping_pending が偽で、開始も終了もある行）は null（夜勤休憩を空へ戻さない）。
 *
 * @returns 印なら { kind, nightBreakMinutes }、違えば null
 */
export function stampedNightOriginOf (row, { hasStart, hasEnd, future } = {}) {
  const kind = stampedKindOf(row, { hasStart, hasEnd, future })
  if (!kind) return null
  return {
    kind,
    nightBreakMinutes: wholeMinutes(row.stamped_night_break_minutes)
  }
}

/**
 * 勤務表の1日の行が「処理済みの打刻日」になりうる行か（(ウ) の候補）。
 *
 * ## 何のためか
 *
 * 打刻した日は、勤務時間の計算が記録されたあとも、休憩に「打刻にしか無い事実」が入ったまま残る
 * （打刻した休憩の分が足されている／固定休憩を使わない会社は案件の休憩が入っていない）。
 * その値は、勤務表の自動計算が出す値とは違うので、何もしなければ「手で直した休憩」として固定され、
 * あとから終了を直しても、定時後の休憩がついていかない。利用者は手で直していないのに。
 *
 * そこで、保存済みの休憩が「自動の値へ、打刻の事実を重ねた値」と同じ行は、固定せず、
 * 休憩の自動計算が走るたびに、同じ事実を重ね直す。
 *
 * ## ここで決めること・決めないこと
 *
 * ここは、一覧の行の印から「重ねる事実がある行か」だけを決める。保存済みの休憩が、重ねた値と
 * 同じかどうかは、勤務表（Roster.js の markPersistedRestOverrides）が比べる
 * （自動の値は、勤務表の本体の式でしか出せないため）。
 *
 * 候補にしない行：
 * - 印が付いてこない応答（印を返さない版のサーバ）。今までどおりになる。
 * - 勤務時間がまだ計算されていない日（stamping_pending）。(ア) の扱い。
 * - 開始か終了が画面に無い行。(イ) か、今までどおり。
 * - 時刻を隠して返された行・画面が未来日として扱う行・行が無い日。
 * - 打刻した休憩が 0 分の行。
 *   - 固定休憩を使う会社：重ねる事実が無い（自動の値そのものと比べれば足りる。今までの決まりと同じ）。
 *   - 固定休憩を使わない会社：一覧の行の印では、打刻で始めた日か、勤務表で入れて休憩を手で直した日かを
 *     見分けられない（どちらも、休憩が案件の休憩ぶん少ない）。候補にすると、勤務表で入れて休憩を
 *     「00:10」「00:00」と手で直した日まで、時刻を直すたびに計算し直されてしまう。
 *     手で直した休憩を守る側へ倒す（自動の値と違えば固定する。今までの決まりと同じ結果）。
 *     打刻した休憩が1分以上ある行は、打刻した日だと分かるので、候補にする。
 *
 * 「休憩が打刻の入れた値のままか」の印（stamped_rest_untouched）は見ない。
 * 定時後休憩が足された処理済みの行は、打刻が入れた値とは違うので、その印は false になる。
 *
 * @returns 候補なら { kind: 'settled', fixedBreak, breakMinutes, nightBreakMinutes }、違えば null
 */
export function settledStampedOriginOf (row, { hasStart, hasEnd, future } = {}) {
  if (!hasStampedFacts(row)) return null
  if (future) return null
  if (row.actual_hidden === true) return null
  if (row.id === null || row.id === undefined) return null
  if (row.stamping_pending !== false) return null
  if (!hasStart || !hasEnd) return null

  const fixedBreak = row.stamping_fixed_break === true
  const breakMinutes = wholeMinutes(row.stamped_break_minutes)
  // 打刻した休憩が無い行は、候補にしない（固定休憩を使う会社でも、使わない会社でも）。
  if (breakMinutes === 0) return null

  return {
    kind: STAMPED_KIND_SETTLED,
    fixedBreak,
    breakMinutes,
    nightBreakMinutes: wholeMinutes(row.stamped_night_break_minutes)
  }
}

/**
 * 開始も終了も保存してあった行の終了を、勤務表で消したとき、休憩を戻す先の事実。
 *
 * ## 何のためか
 *
 * 退勤を押し間違えた日は、打刻をやり直せない（出勤のボタンは「本日はすでに退勤済みです。勤務表から
 * 修正してください。」と断る）。利用者は、勤務表で終了を消して保存し、あとで退勤を打刻する。
 * 勤務表の自動計算は、終了が空の行の休憩を空にする。空のまま保存されると、そのあとの退勤の打刻のあと、
 * 休憩が空の行は「休憩を手で空にした日」と見分けが付かず、休憩なしで記録される
 * （時間外が、休憩の分だけ多い）。
 *
 * そこで、終了を消した行の休憩は、空にせず、出勤だけ打刻した日と同じ値へ戻す：
 * 固定休憩を使う会社は「案件の休憩＋打刻した休憩」、使わない会社は「打刻した休憩」。
 * そのあと退勤を打刻すれば、同じ時刻を打刻しただけの日と同じ結果になる。
 *
 * ## 当てる行・当てない行
 *
 * 当てるのは、一覧が開始も終了も返した、保存済みの行（勤務表で終了を消す前は、開始も終了もあった行）。
 * - 印が付いてこない応答（印を返さない版のサーバ）には当てない（今までどおり、休憩を空にする）。
 * - まだ行が無い日・一覧が終了を返していない行（勤務表で開始だけ入れた行）には当てない
 *   （今までどおり。出勤だけ打刻した日は、stampedOriginOf の (イ) が別に扱う）。
 * - 時刻を隠して返された行・画面が未来日として扱う行には当てない。
 * 開始も消したときに戻さないこと・戻す値が 0 分なら空のままにすることは、呼ぶ側（Roster.js）が見る。
 *
 * @returns 当てる行なら { fixedBreak, breakMinutes }、違えば null
 */
export function endClearedRestFactsOf (row, { future } = {}) {
  if (!hasStampedFacts(row)) return null
  if (future) return null
  if (row.actual_hidden === true) return null
  if (row.id === null || row.id === undefined) return null
  if (!hasText(row.start_time) || !hasText(row.end_time)) return null
  return {
    fixedBreak: row.stamping_fixed_break === true,
    breakMinutes: wholeMinutes(row.stamped_break_minutes)
  }
}

const hasText = value => value !== null && value !== undefined && String(value) !== ''

/** 一覧の行が、打刻の印を持っているか（印を返す版のサーバの応答か）。 */
export function hasStampedFacts (row) {
  return Boolean(row) && typeof row === 'object' &&
    Object.prototype.hasOwnProperty.call(row, 'stamping_pending')
}

const SERVER_TODAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/

/**
 * 一覧の応答が持つ「サーバの今日」（'YYYY-MM-DD'）。読めなければ null。
 *
 * ## 何のためか
 *
 * 打刻のあとに見えない所で流す保存（RosterHeadless.js）は、「今日より後の日」をこの日付で決める
 * （まだ行が無い日をどこまで送ってよいか＝新しい行を作ってよい日の終点、と、未来日として時刻を送らない日）。
 * 端末の日付では決めない。端末の日付が進んでいると、サーバから見て明日の、時刻の無い行
 * （日付・勤怠・案件だけ）がサーバに出来てしまう（サーバの未来日の検査は、時刻のある行しか断らない）。
 * サーバは、一覧が未来日の時刻を隠すときと同じ今日を、全部の行（行が無い日も）へ載せて返す。
 *
 * ## null を返すとき（見えない保存は進めない）
 *
 * - 行が1つも無い。
 * - 項目を持たない行が1つでもある（項目を返さない版のサーバの応答）。
 * - 'YYYY-MM-DD' の形でない値・行によって違う値がある（どれを信じてよいか分からない）。
 *
 * 見える勤務表は、これを使わない（今までどおり端末の日付で動く）。
 */
export function serverTodayOf (rows) {
  const list = Array.isArray(rows) ? rows : []
  if (!list.length) return null
  let today = null
  for (const row of list) {
    const value = row && typeof row === 'object' ? row.server_today : undefined
    if (typeof value !== 'string' || !SERVER_TODAY_PATTERN.test(value)) return null
    if (today === null) today = value
    else if (today !== value) return null
  }
  return today
}

/** 0 以上の整数の分。読めなければ 0。 */
function wholeMinutes (value) {
  const minutes = Number(value)
  return Number.isFinite(minutes) && minutes > 0 ? Math.floor(minutes) : 0
}

/**
 * 'HH:mm'（負の値は先頭に '-'）を分にする。読めなければ null。
 * 勤務表の欄の値と、画面が出した勤務時間（'-00:20' の形になることがある）を読むのに使う。
 */
export function clockToMinutes (value) {
  const matched = /^(-?)(\d{1,3}):([0-5]\d)$/.exec(String(value === null || value === undefined ? '' : value).trim())
  if (!matched) return null
  const minutes = Number(matched[2]) * 60 + Number(matched[3])
  return matched[1] ? -minutes : minutes
}

/** 分（0 以上）を 'HH:mm' にする。 */
export function minutesToClock (minutes) {
  const whole = Math.max(0, Math.floor(Number(minutes) || 0))
  return `${String(Math.floor(whole / 60)).padStart(2, '0')}:${String(whole % 60).padStart(2, '0')}`
}
